import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Divider, Form, FormInstance, Input, Modal, notification, Row, Select, Space, Table, Tabs, Tag, Typography } from "antd";
import { CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { FaBriefcase, FaRegUser } from "react-icons/fa";
import locale from 'antd/es/locale/pt_BR';
import Highlighter from "react-highlight-words";
import moment from "moment";

import { IOrgao } from "../../../../Orgaos/interfaces";
import { IGetUsuarios, IUpdateCategoriasUsuario } from "./interfaces";
import { ICategoria, ISolicitacaoAtivacao, IUsuarioSolicitacao } from "../../../interfaces";

import { EmitirErro } from "../../../../../../services/EmitirErro";
import SolicitacoesDeAtivacaoService from "../../../../../../services/http/services/SaldoDeContas/SolicitacoesDeAtivacaoService";
import InputObservacaoEtapa from "../../components/InputObservacaoEtapa";
import { useGetColumnSearchPropsData } from "../../../../../../hooks/FilterTable/date";
import { removeAcento } from "../../../../../../utils/RemoveAcentos";
import EtapaInfo from "../../components/EtapaInfo";
import CardInformacao from "../../../../components/CardInformacao";
import BotaoLimparFiltros, { checkHasActiveFilters } from "../../../../components/BotaoLimparFiltros";
import BotaoLimparSelecao from "../../../../components/BotaoLimparSelecao";

interface IProps {
    solicitacao: ISolicitacaoAtivacao | null;
    onUpdateOrgao: (orgao: IOrgao) => void;
    onEtapaIniciada: (solicitacao: ISolicitacaoAtivacao) => void;
    onEtapaConcluida: (solicitacao: ISolicitacaoAtivacao) => void;
    onUpdateObservacao: (solicitacao: ISolicitacaoAtivacao) => void;
    onGetUsuarios: (data: IGetUsuarios) => Promise<IUsuarioSolicitacao[]>;
    onUpdateCategoriasUsuario: (data: IUpdateCategoriasUsuario) => Promise<IUsuarioSolicitacao | null>;
    isManutencao?: boolean;
}

export const PermissoesUsuarios = ({
    solicitacao,
    onUpdateOrgao,
    onEtapaIniciada,
    onEtapaConcluida,
    onUpdateObservacao,
    onGetUsuarios,
    onUpdateCategoriasUsuario,
    isManutencao = false,
}: IProps) => {

    const [usuarios, setUsuarios] = useState<IUsuarioSolicitacao[] | null>(null);
    const [categorias, setCategorias] = useState<ICategoria[] | null>(null);
    const [isLoadingUsuarios, setIsLoadingUsuarios] = useState<boolean>(true);
    const [hasOneRowSelected, setHasOneRowSelected] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([]);
    const [selectedUsuario, setSelectedUsuario] = useState<IUsuarioSolicitacao | null>(null);
    const [isPermissoesModalVisible, setIsPermissoesModalVisible] = useState<boolean>(false);
    const [isSubmitingIdAplicacao, setIsSubmitingPermissoes] = useState<boolean>(false);

    const formPermissoesRef = useRef<FormInstance<any>>(null);

    const etapa = solicitacao?.etapasSolicitacaoAtivacao?.find((e) => e.etapa.ordem === 4);

    const onSelectChange = useCallback((selectedRowKeys): void => {
        if (selectedRowKeys.length > 1) {
            setSelectedRowKeys([selectedRowKeys[1]]);
            setHasOneRowSelected(true);
        } else if (selectedRowKeys.length === 0) {
            setSelectedRowKeys(selectedRowKeys);
            setHasOneRowSelected(false);
        } else {
            setSelectedRowKeys([selectedRowKeys[0]]);
            setHasOneRowSelected(true);
        }
    }, [selectedRowKeys]);

    const handleClickAdicionarTodasCategorias = useCallback(() => {
        formPermissoesRef.current?.setFieldsValue({ categorias: categorias?.map((e) => e.id) || [] });
    }, [categorias]);

    const handleClickRemoverTodasCategorias = useCallback(() => {
        formPermissoesRef.current?.setFieldsValue({ categorias: [] });
    }, []);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: '1%',
        type: 'radio' as any
    };

    const handlePermissoes = useCallback(() => {
        try {
            formPermissoesRef.current?.validateFields()
                .then(async (values: any) => {
                    if (!solicitacao || !selectedUsuario) {
                        return;
                    }

                    try {
                        setIsSubmitingPermissoes(true);

                        const updatedUsuarioResponse = await onUpdateCategoriasUsuario({
                            solicitacaoId: solicitacao.id,
                            usuarioId: values.usuarioId,
                            categoriaIds: values.categorias,
                        });

                        if (!updatedUsuarioResponse) {
                            return;
                        }

                        notification.success({
                            message: 'Sucesso',
                            description: 'Permissões atualizadas com sucesso!',
                        });

                        setIsSubmitingPermissoes(false);
                        setUsuarios((prevState) => {
                            if (!prevState) {
                                return null;
                            }

                            const newData = [...prevState];

                            const orgaoIndex = newData.findIndex((e) => e.id === updatedUsuarioResponse.id);

                            if (orgaoIndex >= 0) {
                                newData[orgaoIndex] = updatedUsuarioResponse;
                            }

                            return newData;
                        });

                        handleCloseModal();
                    } catch (error) {
                        setIsSubmitingPermissoes(false);
                        EmitirErro(error, true, 'updatePermissoes_0213asldo2o3w0jasdmo0j2o');
                    }
                })
                .catch(() => {
                    notification.error({
                        message: 'Erro',
                        description:
                            'Preencha os campos obrigatorios!',
                    });
                });

        } catch (error) {
            EmitirErro(error, true, 'updatePermissoes_0213asldo2o3w0jasdmo0j2o');
        }
    }, [selectedUsuario, solicitacao]);

    const clickBotaoEditarUsuario = useCallback((): void => {
        if (selectedRowKeys.length == 1) {
            const usuarioSelecionado = usuarios?.find((e) => e.id === selectedRowKeys[0]);

            if (usuarioSelecionado) {
                setSelectedUsuario(usuarioSelecionado);

                formPermissoesRef.current?.setFieldsValue({
                    usuarioId: usuarioSelecionado.id,
                    categorias: usuarioSelecionado.categorias.map((e) => e.id),
                });

                setIsPermissoesModalVisible(true);
            }
        }
    }, [selectedRowKeys, usuarios]);

    const findData = async () => {
        try {
            if (!solicitacao) {
                return;
            }

            setIsLoadingUsuarios(true);

            const [
                usuariosData,
                categoriasData,
            ] = await Promise.all([
                onGetUsuarios({ solicitacaoId: solicitacao.id }),
                SolicitacoesDeAtivacaoService.getCategorias({}),
            ]);

            setUsuarios(usuariosData);
            setCategorias(categoriasData);
            setIsLoadingUsuarios(false);
        } catch (error) {
            setIsLoadingUsuarios(false);
            EmitirErro(error, true, 'findUsuariosCategorias_203oasdjasdno08jo12');
        }
    }

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setHasOneRowSelected(false);
    }, []);

    const handleSearch = useCallback((selectedKeys, confirm, dataIndex): void => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    }, []);

    const handleReset = useCallback((clearFilters): void => {
        clearFilters();
        setSearchText('');
    }, []);

    const onclickTable = useCallback((record: IUsuarioSolicitacao, row: any): void => {

        if (selectedRowKeys.includes(record.id)) {
            let filter = selectedRowKeys.filter((val) => {
                return val != record.id
            })
            if (filter.length === 1) {
                setSelectedRowKeys(filter);
                setHasOneRowSelected(true);
            } else if (filter.length === 0) {
                setSelectedRowKeys(filter);
                setHasOneRowSelected(false);
            } else {
                setSelectedRowKeys(filter);
            }

        } else {
            if (selectedRowKeys.length == 0) {
                setSelectedRowKeys([record.id]);
                setHasOneRowSelected(true);
            } else {
                setSelectedRowKeys([record.id]);
                setHasOneRowSelected(true);
            }
        }
    }, [selectedRowKeys]);

    const getColumnSearchProps = useCallback((dataIndex) => {
        let searchInput: any = '';
        return ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={node => {
                            searchInput = node;
                        }}
                        placeholder={`Digite...`}
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: any) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value: any, record: any) =>
                record[dataIndex] ? removeAcento(record[dataIndex].toString()).includes(removeAcento(value)) : false,
            onFilterDropdownVisibleChange: (visible: any) => {
                if (visible) {
                    setTimeout(() => searchInput.select());
                }
            },
            render: (text: any) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                ),
        })
    }, [searchText, searchedColumn, handleSearch, handleReset]);

    const columnSearchPropsDataCriadoEm = useGetColumnSearchPropsData('createdAt');

    const handleClearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setSearchText('');
        setSearchedColumn('');
    }, []);

    const hasActiveFilters = useMemo(() => {
        return checkHasActiveFilters(filteredInfo, searchText);
    }, [filteredInfo, searchText]);

    const handleCloseModal = useCallback(() => {
        setSelectedUsuario(null);
        setIsPermissoesModalVisible(false);
        formPermissoesRef.current?.setFieldsValue({
            usuarioId: '',
            categorias: [],
        });
    }, []);

    const columns = [
        {
            title: 'Nome',
            dataIndex: 'nome',
            key: 'nome',
            filteredValue: filteredInfo.nome || null,
            ...getColumnSearchProps('nome'),
        },
        {
            title: 'Cargo',
            dataIndex: 'cargo',
            key: 'cargo',
            filteredValue: filteredInfo.cargo || null,
            ...getColumnSearchProps('cargo'),
        },
        {
            title: 'Categorias',
            dataIndex: 'categorias',
            key: 'categorias',
            width: '18%',
            render: (categorias: ICategoria[]) => {
                return (
                    <Tag>{categorias.length} categorias permitidas</Tag>
                )
            }
        },
        {
            title: 'Criado em',
            dataIndex: 'createdAt',
            key: 'createdAt',
            filteredValue: filteredInfo.createdAt || null,
            ...columnSearchPropsDataCriadoEm,
            width: '11%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.concluidaEm) {
                    aMonMen = moment(a.concluidaEm).unix()
                } else {
                    aMonMen = 0
                }
                if (b.concluidaEm) {
                    bMonMen = moment(b.concluidaEm).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (criadoEm: string) => (
                criadoEm ? moment(criadoEm).format('DD/MM/YYYY HH:mm') : null
            ),
        },
    ];

    useEffect(() => {
        findData();
    }, []);

    const items = useMemo(() => {
        if (!solicitacao) {
            return;
        }

        return [
            {
                key: 'permissoes-usuarios',
                label: 'Permissões',
                children: (
                    <>
                        <Col span={24}>
                            <div
                                style={{
                                    position: 'sticky',
                                    top: 84,
                                    zIndex: 2,
                                    width: 'fit-content'
                                }}
                            >
                                <Row>
                                    <Col>
                                        <Button type="primary"
                                            style={{ marginRight: 5, marginBottom: 5 }}
                                            className="btn_warning_cewuyfgfvewofc"
                                            onClick={clickBotaoEditarUsuario}
                                            disabled={!hasOneRowSelected}
                                        >
                                            Gerenciar permissões
                                        </Button>
                                    </Col>

                                    <Col>
                                        <BotaoLimparFiltros
                                            hasActiveFilters={hasActiveFilters}
                                            onClick={handleClearAllFilters}
                                        />
                                    </Col>

                                    <Col>
                                        <BotaoLimparSelecao
                                            hasSelectedRows={selectedRowKeys.length > 0}
                                            onClick={handleResetRowSelection}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            <div className='headerAbaixodosBtnTableList_ihabdfvohewvbfopu' />

                            <Table
                                rowKey={(record) => record.id}
                                style={{ marginTop: -34 }}
                                size="small"
                                onRow={(record, rowIndex) => {
                                    return {
                                        onClick: event => { onclickTable(record, rowIndex) },
                                    };
                                }}
                                rowSelection={rowSelection}
                                columns={columns}
                                dataSource={usuarios || []}
                                pagination={{ locale: { items_per_page: '' } }}

                                locale={locale.Table}
                                loading={isLoadingUsuarios}
                                onChange={(pagination, filters, sorter, extra) => {
                                    setFilteredInfo(filters || {});
                                    handleResetRowSelection();
                                }}
                            />
                        </Col>
                    </>
                )
            },
            !isManutencao ? (
                {
                    key: 'demais-configuracoes-etapa',
                    label: 'Demais configurações da etapa',
                    children: (
                        <>
                            <InputObservacaoEtapa
                                solicitacaoId={solicitacao.id}
                                etapa={etapa}
                                onUpdateObservacao={onUpdateObservacao}
                                style={{
                                    padding: 0,
                                }}
                            />
                        </>
                    )
                }
            ) : undefined
        ].filter((e) => e !== undefined) as any[];
    }, [
        solicitacao,
        onEtapaConcluida,
        onUpdateObservacao,
        onUpdateOrgao,
        etapa,
        usuarios,
        clickBotaoEditarUsuario,
        hasOneRowSelected,
        rowSelection,
        columns,
        isLoadingUsuarios,
        handleResetRowSelection,
        handleClearAllFilters,
        hasActiveFilters,
    ]);

    if (!solicitacao) {
        return null;
    }

    return (
        <>
            <Row>
                {
                    !isManutencao && (
                        <EtapaInfo
                            iniciadaEm={etapa?.iniciadaEm}
                            concluidaEm={etapa?.concluidaEm}
                            descricao={etapa?.etapa.descricao}
                            solicitacaoId={solicitacao.id}
                            etapa={etapa}
                            onEtapaIniciada={onEtapaIniciada}
                            onEtapaConcluida={onEtapaConcluida}
                        />
                    )
                }

                <div
                    style={{
                        width: '100%',
                        ...(!isManutencao ? {
                            opacity: etapa?.iniciadaEm ? 1 : 0.5,
                            pointerEvents: etapa?.iniciadaEm ? 'all' : 'none'
                        } : {}),
                    }}
                >
                    <Tabs
                        type="line"
                        items={items}
                        renderTabBar={isManutencao ? () => <div style={{ marginTop: 15 }} /> : undefined}
                        style={{
                            margin: '0 10px',
                        }}
                    />
                </div>
            </Row >


            <Modal
                title={selectedUsuario ? "Editar permissões do usuário" : "Cadastrar permissões do usuário"}
                open={isPermissoesModalVisible}
                onCancel={handleCloseModal}
                footer={null}
                width={800}
            >
                <Row
                    gutter={[8, 8]}>
                    <Col
                        xs={24}
                        sm={12}
                    >
                        <CardInformacao
                            IconComponent={FaRegUser}
                            title="NOME"
                            value={selectedUsuario?.nome || "--"}
                            iconSize={13}
                            fontSize={12}
                            style={{
                                padding: '4px 6px'
                            }}
                        />
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                    >
                        <CardInformacao
                            IconComponent={FaBriefcase}
                            title="CARGO"
                            value={selectedUsuario?.cargo || "--"}
                            iconSize={13}
                            fontSize={12}
                            style={{
                                padding: '4px 6px'
                            }}
                        />
                    </Col>
                </Row>

                <Divider />

                <Form
                    ref={formPermissoesRef}
                    style={{ marginTop: 10 }}
                    layout="vertical"
                    initialValues={{
                        usuarioId: selectedUsuario?.id,
                        categorias: selectedUsuario?.categorias.map((e) => e.id) || [],
                    }}
                >
                    <Form.Item name="usuarioId" hidden>
                        <Input />
                    </Form.Item>

                    <div style={{ marginTop: 10 }}>
                        <Typography.Text>
                            Selecione as categorias permitidas:
                        </Typography.Text>

                        <Form.Item
                            name="categorias"
                        >
                            <Select
                                style={{ width: "100%", marginTop: 10 }}
                                placeholder="Selecione as categorias permitidas"
                                mode="multiple"
                                allowClear
                                dropdownRender={menu => (
                                    <div>

                                        <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                                            <Button
                                                block
                                                danger
                                                onClick={handleClickRemoverTodasCategorias}
                                                type="primary"
                                                style={{
                                                    width: 80,
                                                    marginRight: 10
                                                }}
                                            >
                                                <CloseCircleOutlined />
                                            </Button>

                                            <Button
                                                block
                                                onClick={handleClickAdicionarTodasCategorias}
                                                type="dashed"
                                            >Todos</Button>
                                        </div>

                                        <Divider style={{ margin: '4px 0' }} />

                                        {menu}
                                    </div>
                                )}
                            >
                                {
                                    categorias?.map((categoria) => (
                                        <Select.Option
                                            value={categoria.id}
                                            key={categoria.id}
                                        >
                                            {categoria.nome}
                                        </Select.Option>
                                    ))
                                }
                            </Select>
                        </Form.Item>
                    </div>

                    <Row>
                        <Col span={24} style={{ textAlign: "end" }}>
                            <Button
                                style={{ marginRight: 10 }}
                                onClick={handleCloseModal}
                            >
                                Cancelar
                            </Button>

                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={handlePermissoes}
                                loading={isSubmitingIdAplicacao}
                            >
                                Salvar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    )
}
